import {
  AI_LABEL_ASSETS,
  AI_LABEL_COLOUR_HEX,
  AI_LABEL_MOVE_STEP,
  AI_LABEL_MOVE_STEP_LARGE,
  AI_LABEL_RESIZE_STEP,
  AI_LABEL_TEXT_SHADOW,
  AiLabelPosition,
  AiLabelType
} from './aiLabel.constants'
import {
  AiLabelRect,
  AiLabelSnapGuides,
  clampRectToImage,
  clampSizeFraction,
  getAiLabelBoxSize,
  getAiLabelRect,
  rectToPosition,
  snapRect
} from './aiLabelLayout'
import { BLUE_600, GRAY_400, InterVariableFontFamily } from 'constants/styling/theme'
import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react'

import { logger } from 'utils/logging'
import { shouldApplyAiLabel } from './applyAiLabelToImage'
import { useClientPhotoStudio } from '../ClientPhotoStudio.context'
import { useTranslation } from 'react-i18next'

interface ContentRect {
  /** Offset of the rendered image content inside the preview container, in CSS px */
  left: number
  top: number
  /** Rendered image content size, in CSS px */
  width: number
  height: number
  /** CSS px per image px */
  scale: number
  /** Natural image size, in image px */
  naturalWidth: number
  naturalHeight: number
}

type Corner = 'nw' | 'ne' | 'sw' | 'se'

interface Gesture {
  kind: 'move' | 'resize'
  pointerId: number
  startClientX: number
  startClientY: number
  startRect: AiLabelRect
  startSizeFraction: number
  corner?: Corner
  /** True once the pointer travelled beyond the click threshold - a bare click commits nothing */
  hasMoved: boolean
  /** Live values while the gesture is held (committed to context on release) */
  position: AiLabelPosition
  sizeFraction: number
  guides: AiLabelSnapGuides
}

export interface AiLabelOverlayProps {
  /** Ref to the preview <img> element (object-fit: contain) the overlay mirrors */
  imgRef: RefObject<HTMLImageElement | null>
}

const CORNERS: Corner[] = ['nw', 'ne', 'sw', 'se']

const cornerCursor: Record<Corner, string> = {
  nw: 'nwse-resize',
  se: 'nwse-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
}

/**
 * AiLabelOverlay — live preview and direct-manipulation surface for the AI label
 * on top of the staging preview image. Drag the label to move it, drag a corner
 * to resize (aspect locked, opposite corner pinned); arrows / [ ] for keyboard.
 * The actual pixels are only written on Save (applyAiLabelToImage), using the same
 * rect from aiLabelLayout so preview and export match.
 *
 * During a gesture the live position/size lives in local state and is committed
 * to the studio context once on release, keeping per-move re-renders local.
 */
export const AiLabelOverlay: React.FC<AiLabelOverlayProps> = ({ imgRef }) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const {
    aiLabelState,
    setAiLabelPosition,
    setAiLabelSizeFraction,
  } = useClientPhotoStudio()

  const [contentRect, setContentRect] = useState<ContentRect | null>(null)
  const [gesture, setGesture] = useState<Gesture | null>(null)
  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const gestureRef = useRef<Gesture | null>(null)
  gestureRef.current = gesture

  const updateContentRect = useCallback(() => {
    const img = imgRef.current
    if (!img) {
      return
    }

    const { naturalWidth, naturalHeight, clientWidth, clientHeight, offsetLeft, offsetTop } = img
    if (!naturalWidth || !naturalHeight || !clientWidth || !clientHeight) {
      setContentRect(null)
      return
    }

    const scale = Math.min(clientWidth / naturalWidth, clientHeight / naturalHeight)
    const width = naturalWidth * scale
    const height = naturalHeight * scale

    setContentRect({
      left: offsetLeft + (clientWidth - width) / 2,
      top: offsetTop + (clientHeight - height) / 2,
      width,
      height,
      scale,
      naturalWidth,
      naturalHeight,
    })
  }, [imgRef])

  useEffect(() => {
    const img = imgRef.current
    if (!img) {
      return
    }

    const handleImgError = () => {
      // The live label/disclaimer preview disappears with the image - make that visible in Sentry
      logger.warn('AI label overlay hidden: preview image failed to load', undefined, {
        context: { component: 'AiLabelOverlay', action: 'previewImageError' }
      })
      updateContentRect()
    }

    updateContentRect()
    img.addEventListener('load', updateContentRect)
    img.addEventListener('error', handleImgError)

    const observer = new ResizeObserver(updateContentRect)
    observer.observe(img)

    return () => {
      img.removeEventListener('load', updateContentRect)
      img.removeEventListener('error', handleImgError)
      observer.disconnect()
    }
  }, [imgRef, updateContentRect])

  const showLabel = shouldApplyAiLabel(aiLabelState)

  const imageSize = contentRect ? { width: contentRect.naturalWidth, height: contentRect.naturalHeight } : null

  const layoutInput = {
    labelType: aiLabelState.labelType,
    labelText: aiLabelState.labelText.trim(),
    position: gesture?.position ?? aiLabelState.position,
    sizeFraction: gesture?.sizeFraction ?? aiLabelState.sizeFraction,
  }

  const rect = imageSize && showLabel ? getAiLabelRect(layoutInput, imageSize) : null

  const beginGesture = (event: React.PointerEvent, kind: Gesture['kind'], corner?: Corner) => {
    // Primary button/pointer only - right-clicks and extra fingers must not start or steal a gesture
    if (!rect || !imageSize || event.button !== 0 || gestureRef.current) {
      return
    }
    event.preventDefault()
    event.stopPropagation()
    ;(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)

    const startSizeFraction = layoutInput.sizeFraction
      ?? clampSizeFraction(layoutInput, aiLabelState.labelType === AiLabelType.TEXT ? (rect.fontSize ?? 0) / imageSize.width : rect.width / imageSize.width, imageSize)

    setGesture({
      kind,
      corner,
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startRect: rect,
      startSizeFraction,
      hasMoved: false,
      position: rectToPosition(rect, imageSize),
      sizeFraction: startSizeFraction,
      guides: {},
    })
  }

  const handlePointerMove = (event: React.PointerEvent) => {
    const current = gestureRef.current
    if (!current || !contentRect || !imageSize || event.pointerId !== current.pointerId) {
      return
    }
    event.preventDefault()

    const clientDistance = Math.hypot(event.clientX - current.startClientX, event.clientY - current.startClientY)
    const hasMoved = current.hasMoved || clientDistance > 3

    const dx = (event.clientX - current.startClientX) / contentRect.scale
    const dy = (event.clientY - current.startClientY) / contentRect.scale

    if (current.kind === 'move') {
      const moved = clampRectToImage({ ...current.startRect, left: current.startRect.left + dx, top: current.startRect.top + dy }, imageSize)
      const { rect: snapped, guides } = snapRect(moved, imageSize)
      setGesture({ ...current, hasMoved, position: rectToPosition(snapped, imageSize), guides })
      return
    }

    // Resize: the grabbed corner follows the pointer, the opposite corner stays pinned.
    // The scale factor comes from the WIDTH ratio only (per spec) - with aspect locked,
    // a height-based ratio would explode for wide, thin boxes (text line, pill)
    const corner = current.corner ?? 'se'
    const pinX = corner.includes('w') ? current.startRect.left + current.startRect.width : current.startRect.left
    const pinY = corner.includes('n') ? current.startRect.top + current.startRect.height : current.startRect.top
    const grabX = corner.includes('w') ? current.startRect.left : current.startRect.left + current.startRect.width

    // Signed projection: crossing over the pinned corner parks at the minimum instead of mirroring
    const direction = corner.includes('w') ? -1 : 1
    const factor = current.startRect.width > 0
      ? Math.max(0, direction * ((grabX + dx) - pinX) / current.startRect.width)
      : 1

    const sizeFraction = clampSizeFraction(layoutInput, current.startSizeFraction * factor, imageSize)
    const box = getAiLabelBoxSize(layoutInput, sizeFraction, imageSize)

    const resized = clampRectToImage({
      left: corner.includes('w') ? pinX - box.width : pinX,
      top: corner.includes('n') ? pinY - box.height : pinY,
      width: box.width,
      height: box.height,
      fontSize: box.fontSize,
    }, imageSize)

    setGesture({ ...current, hasMoved, sizeFraction, position: rectToPosition(resized, imageSize), guides: {} })
  }

  const endGesture = (event: React.PointerEvent) => {
    const current = gestureRef.current
    if (!current || event.pointerId !== current.pointerId) {
      return
    }
    // A bare click (no movement) must not silently switch to a custom placement
    if (current.hasMoved) {
      setAiLabelPosition(current.position)
      if (current.kind === 'resize') {
        setAiLabelSizeFraction(current.sizeFraction)
      }
    }
    setGesture(null)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!rect || !imageSize) {
      return
    }

    const moveStep = event.shiftKey ? AI_LABEL_MOVE_STEP_LARGE : AI_LABEL_MOVE_STEP
    const resizeSteps = AI_LABEL_RESIZE_STEP[aiLabelState.labelType === AiLabelType.TEXT ? 'text' : 'badge']
    const resizeStep = event.shiftKey ? resizeSteps.large : resizeSteps.small

    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      event.preventDefault()
      const dx = event.key === 'ArrowLeft' ? -moveStep : event.key === 'ArrowRight' ? moveStep : 0
      const dy = event.key === 'ArrowUp' ? -moveStep : event.key === 'ArrowDown' ? moveStep : 0
      const moved = clampRectToImage({
        ...rect,
        left: rect.left + dx * imageSize.width,
        top: rect.top + dy * imageSize.height,
      }, imageSize)
      setAiLabelPosition(rectToPosition(moved, imageSize))
      return
    }

    if (event.key === '[' || event.key === ']') {
      event.preventDefault()
      const currentFraction = layoutInput.sizeFraction
        ?? (aiLabelState.labelType === AiLabelType.TEXT ? (rect.fontSize ?? 0) / imageSize.width : rect.width / imageSize.width)
      const next = clampSizeFraction(layoutInput, currentFraction + (event.key === ']' ? resizeStep : -resizeStep), imageSize)
      setAiLabelSizeFraction(next)
      // Keep the centre where it is so resizing doesn't wander
      setAiLabelPosition(rectToPosition(rect, imageSize))
    }
  }

  if (!contentRect || !imageSize || !showLabel) {
    return null
  }

  const { scale } = contentRect
  const isActive = isHovered || isFocused || !!gesture

  return (
    <div
      style={{
        position: 'absolute',
        left: contentRect.left,
        top: contentRect.top,
        width: contentRect.width,
        height: contentRect.height,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >


      {/* Snap guides while dragging */}
      {gesture?.guides.vertical !== undefined && (
        <div style={{ position: 'absolute', left: gesture.guides.vertical * scale, top: 0, bottom: 0, width: 1, backgroundColor: BLUE_600 }} />
      )}
      {gesture?.guides.horizontal !== undefined && (
        <div style={{ position: 'absolute', top: gesture.guides.horizontal * scale, left: 0, right: 0, height: 1, backgroundColor: BLUE_600 }} />
      )}

      {/* The AI label - draggable, corner-resizable, keyboard-operable */}
      {showLabel && rect && (
        <div
          role="img"
          aria-label={t('ai_label.title')}
          tabIndex={0}
          onPointerDown={(e) => beginGesture(e, 'move')}
          onPointerMove={handlePointerMove}
          onPointerUp={endGesture}
          onPointerCancel={endGesture}
          onLostPointerCapture={endGesture}
          onPointerEnter={() => setIsHovered(true)}
          onPointerLeave={() => setIsHovered(false)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          style={{
            position: 'absolute',
            left: rect.left * scale,
            top: rect.top * scale,
            width: rect.width * scale,
            height: rect.height * scale,
            pointerEvents: 'auto',
            touchAction: 'none',
            cursor: gesture?.kind === 'move' ? 'grabbing' : 'grab',
            outline: isFocused ? `2px solid ${BLUE_600}` : isHovered || gesture ? `1px solid ${BLUE_600}` : 'none',
            outlineOffset: 1,
          }}
        >

          {aiLabelState.labelType !== AiLabelType.TEXT && (
            <img
              src={AI_LABEL_ASSETS[aiLabelState.labelType][aiLabelState.colour]}
              alt=""
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                display: 'block',
                // Full opacity while held so the client sees exactly what they are placing
                opacity: gesture ? 1 : aiLabelState.opacity / 100,
              }}
            />
          )}

          {aiLabelState.labelType === AiLabelType.TEXT && (
            <span
              style={{
                display: 'block',
                fontSize: (rect.fontSize ?? 0) * scale,
                fontFamily: InterVariableFontFamily,
                lineHeight: 1,
                color: AI_LABEL_COLOUR_HEX[aiLabelState.colour],
                opacity: gesture ? 1 : aiLabelState.opacity / 100,
                textShadow: `0 0 ${(rect.fontSize ?? 0) * 0.12 * scale}px ${AI_LABEL_TEXT_SHADOW[aiLabelState.colour]}`,
                whiteSpace: 'nowrap',
                userSelect: 'none',
              }}
            >
              {layoutInput.labelText}
            </span>
          )}

          {/* Corner resize handles - visible on hover or focus.
              TODO(mobile): Photo Studio is desktop-only today. For touch devices the handles
              are unreachable (no hover, first pointerdown starts a move) and 8px targets are
              too small - handles would need to be always-visible and >=24px with a
              tap-to-select step. Revisit together with a Photo Studio mobile pass. */}
          {isActive && CORNERS.map((corner) => (
            <div
              key={corner}
              onPointerDown={(e) => beginGesture(e, 'resize', corner)}
              onPointerMove={handlePointerMove}
              onPointerUp={endGesture}
              onPointerCancel={endGesture}
              onLostPointerCapture={endGesture}
              style={{
                position: 'absolute',
                width: 8,
                height: 8,
                backgroundColor: '#FFFFFF',
                border: `1px solid ${GRAY_400}`,
                boxShadow: '0 1px 4px rgba(19, 24, 29, 0.06)',
                cursor: cornerCursor[corner],
                top: corner.includes('n') ? -4 : undefined,
                bottom: corner.includes('s') ? -4 : undefined,
                left: corner.includes('w') ? -4 : undefined,
                right: corner.includes('e') ? -4 : undefined,
              }}
            />
          ))}

        </div>
      )}
    </div>
  )
}
